import FormatQuote from '@mui/icons-material/FormatQuote';
import Star from '@mui/icons-material/Star';
import type { Theme, TypographyProps } from '@mui/material';
import { Box, Card, Stack, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { FC } from 'react';

import type { TestimonialProps as SanityTestimonialProps } from '~/types/sanity/components';
import { getCompoundTestIdGenerator } from '~/utils/getCompoundTestIdGenerator';

const Title = styled(Typography)<TypographyProps>(({ theme }) => ({
  ...theme.typography.h5,
  marginRight: theme.spacing(),
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.secondary.main,
  fontWeight: 'bolder',
}));

const Paragraph = styled(Typography)(({ theme }) => ({
  ...theme.typography.h6,
  fontWeight: 'regular',
}));
const CustomStar = styled(Star)(({ theme }) => ({
  color: '#F6CA7E',
  marginRight: theme.spacing(0.5),
}));
const CustomQuoteIcon = styled(FormatQuote)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(3),
  width: 68,
  height: 48,
  color: theme.palette.secondary.main,
  opacity: 0.25,
}));

interface TestimonialProps extends Omit<SanityTestimonialProps, '_type'> {
  shouldShowElevation: boolean;
}

export const Testimonial: FC<TestimonialProps> = ({ _key, paragraph, shouldShowElevation, stars, subtitle, title }) => {
  const smUp = useMediaQuery<Theme>((theme) => theme.breakpoints.up('sm'));
  const mdUp = useMediaQuery<Theme>((theme) => theme.breakpoints.up('md'));
  const lgUp = useMediaQuery<Theme>((theme) => theme.breakpoints.up('lg'));
  const getCompoundTestId = getCompoundTestIdGenerator('testimonial');

  const hasHeaderContent = !!(title || stars || subtitle);
  const testimonialCardElevation = shouldShowElevation ? 12 : 1;

  let testimonialContainerWidth = '300px';

  if (lgUp) {
    testimonialContainerWidth = '760px';
  } else if (mdUp) {
    testimonialContainerWidth = '600px';
  } else if (smUp) {
    testimonialContainerWidth = '400px';
  }

  return (
    <Box
      sx={{ px: { xs: 1, sm: 2, md: 3 }, width: testimonialContainerWidth, pt: 8, pb: 5 }}
      data-testid={getCompoundTestId(_key)}
    >
      <Card elevation={testimonialCardElevation}>
        <Stack sx={{ px: 3, py: 6, position: 'relative', gap: 6 }}>
          <CustomQuoteIcon viewBox="5 7 14 10" />

          {hasHeaderContent && (
            <Box>
              <Stack sx={{ flexDirection: 'row', alignItems: 'center', position: 'relative' }}>
                {title && (
                  <Title component="span" data-testid={getCompoundTestId('title', _key)}>
                    {title}
                  </Title>
                )}

                {[...Array(stars)].map((_, i) => (
                  // as the stars amount is static the key prop can rely on the index
                  <CustomStar
                    key={`testimonial-star-${_key}-${i}`}
                    data-testid={getCompoundTestId('star', _key, i.toString())}
                  />
                ))}
              </Stack>

              {subtitle && <Subtitle data-testid={getCompoundTestId('subtitle', _key)}>{subtitle}</Subtitle>}
            </Box>
          )}

          {paragraph && <Paragraph data-testid={getCompoundTestId('paragraph', _key)}>{paragraph}</Paragraph>}
        </Stack>
      </Card>
    </Box>
  );
};
